// =====================================================================
// Firmware + OtaRun zod mirror (Sprint 4, 2026-05-31)
// Updated for broadcast OTA model (Task 1, 2026-06-30)
// =====================================================================
// Mirrors the Rust types in `crates/rhia-protocol/src/ota.rs`.
// =====================================================================

import { z } from 'zod';

export const OtaRunStatusSchema = z.enum([
  'queued',
  'downloading',
  'broadcasting',
  'verifying',
  'repairing',
  'awaiting_activate_confirm',
  'activating',
  'done',
  'failed',
  'cancelled',
]);
export type ParsedOtaRunStatus = z.infer<typeof OtaRunStatusSchema>;

const OtaSlaveStatusSchema = z.object({
  slave_id: z.number().int(),
  received: z.number().int(),
  total: z.number().int(),
  digest_ok: z.boolean(),
  booted_version: z.string().nullable().optional(),
});

export const FirmwareSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  version: z.string().min(1),
  version_idx: z.number().int().nonnegative().default(0),
  target_board_type: z.string().min(1),
  size_bytes: z.number().int().nonnegative(),
  sha256: z.string().min(1),
  signature_hex: z.string().min(1),
  signing_key_id: z.string().min(1),
  storage_path: z.string().min(1),
  notes: z.string().nullable(),
  uploaded_by_uid: z.string().nullable(),
  uploaded_at_ms: z.number().int().nonnegative(),
  archived_at_ms: z.number().int().nullable(),
});
export type ParsedFirmware = z.infer<typeof FirmwareSchema>;

export const OtaRunSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  firmware_id: z.string().min(1),
  target_board_id: z.string().min(1),
  status: OtaRunStatusSchema,
  progress_bytes: z.number().int().nonnegative(),
  from_version: z.string().nullable(),
  to_version: z.string().min(1),
  initiated_by_uid: z.string().nullable(),
  started_at_ms: z.number().int().nonnegative(),
  finished_at_ms: z.number().int().nullable(),
  error_message: z.string().nullable(),
  slaves: z.array(OtaSlaveStatusSchema).default([]),
  /** Set by the operator confirm-activate route; the edge watches this field
   *  to gate the final activate step. Defaults to false on new runs. */
  activate_requested: z.boolean().default(false),
});
export type ParsedOtaRun = z.infer<typeof OtaRunSchema>;

export const CreateOtaRunSchema = z.object({
  firmware_id: z.string().min(1),
  target_board_id: z.string().min(1),
});
export type ParsedCreateOtaRun = z.infer<typeof CreateOtaRunSchema>;

export const UpdateOtaRunProgressSchema = z.object({
  status: OtaRunStatusSchema,
  progress_bytes: z.number().int().nonnegative().nullable(),
  error_message: z.string().nullable(),
});
export type ParsedUpdateOtaRunProgress = z.infer<typeof UpdateOtaRunProgressSchema>;
